import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';
import { UserService } from '@core/services/user/user.service';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { from, of, throwError } from 'rxjs';
import { updatePassword } from 'firebase/auth';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Component({
  selector: 'app-authentication-step',
  templateUrl: './authentication-step.component.html',
  styleUrls: ['./authentication-step.component.scss'],
  standalone: false,
})
export class AuthenticationStepComponent implements OnInit {
  @Input() phone?: string;

  @Output() onClose = new EventEmitter<void>();
  @Output() onComplete = new EventEmitter<void>();
  @Output() onKioskConfirm = new EventEmitter<void>();

  isLoading = true;
  showPasswordForm = false;
  passwordForm: UntypedFormGroup = this.fb.group({
    password: ['', Validators.required],
  });
  signInWithPopupInProgress = false;
  passwordErrorMessage?: string;
  isPasswordChangeInProgress = false;

  kioskParameters = this.settingsSelector.kiosk;

  constructor(
    private readonly marketplaceService: MarketplaceService,
    private readonly authFirebaseService: AuthFirebaseService,
    private readonly userService: UserService,
    private readonly authStoreSelector: AuthStoreSelector,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly fb: UntypedFormBuilder,
    private readonly analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    const comoPlugin = this.marketplaceService.getPluginByType('como');
    const user = this.authStoreSelector.selectUser();
    const allowAnonymousUser = !!comoPlugin?.['allow_anonymous_user'];
    const isKiosk = !!this.kioskParameters.isKioskLayout;
    const showsPasswordForm = !allowAnonymousUser && user?.is_anonymous === true;

    this.analyticsService.log({
      level: showsPasswordForm ? 'warn' : 'info',
      label: '[DOOD-3204] ComoAuthenticationStep.gate',
      message: {
        allow_anonymous_user: allowAnonymousUser,
        is_anonymous: user?.is_anonymous ?? null,
        has_user: !!user?.id,
        is_kiosk: isKiosk,
        shows_password_form: showsPasswordForm,
        has_firebase_user: !!this.authFirebaseService.getCurrentUser(),
        has_dood_guest_session: this.authFirebaseService.hasDoodAnonymousSession(),
      },
    });

    if (allowAnonymousUser || user?.is_anonymous !== true) {
      this.finishLoyaltySession(isKiosk);
      return;
    }

    this.showPasswordForm = true;
    this.isLoading = false;
  }

  googleUpgrade(): void {
    this.signInWithPopupInProgress = true;
    this.authFirebaseService
      .upgradeAnonymousWithGoogle()
      .pipe(
        switchMap(({ firebaseIdToken, userCredential }) =>
          this.authStoreSelector.selectUser$.pipe(
            take(1),
            switchMap(user =>
              this.userService.upgradeAnonymousUser$({}, user?.id ?? '', firebaseIdToken).pipe(
                map(() => userCredential),
                catchError(error => {
                  if (!this.isUpgradeConflict(error)) {
                    return throwError(() => error);
                  }
                  this.analyticsService.log({
                    level: 'info',
                    label: '[DOOD-3204] ComoAuthenticationStep.googleUpgrade',
                    message: { result: 'already_upgraded', status: 409 },
                  });
                  return of(userCredential);
                }),
              ),
            ),
          ),
        ),
        switchMap(userCredential =>
          this.authFirebaseService.saveFirebaseTokenInSessionStore$(userCredential),
        ),
        switchMap(() => this.authFirebaseService.loadUser$()),
        switchMap(user => this.updateUserIsNotAnonymous(user)),
        tap(user => this.handleLoginResult(user)),
      )
      .subscribe({
        error: error => {
          this.signInWithPopupInProgress = false;
          this.passwordErrorMessage = this.authFirebaseService.getPasswordErrorMessage(error);
          this.logGoogleUpgradeError(error);
        },
        complete: () => (this.signInWithPopupInProgress = false),
      });
  }

  private isUpgradeConflict(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status === 409;
  }

  private logGoogleUpgradeError(error: unknown): void {
    const err = error as { code?: string; message?: string; status?: number };
    this.analyticsService.log({
      level: 'error',
      label: '[DOOD-3204] ComoAuthenticationStep.googleUpgrade',
      message: {
        code: err?.code ?? err?.status ?? null,
        message: err?.message ?? null,
      },
    });
  }

  private handleLoginResult(_user?: DoodUserModel) {
    if (!this.kioskParameters.isKioskLayout) {
      this.onComplete.next();
    } else {
      this.onKioskConfirm.next();
    }
  }

  private finishLoyaltySession(isKiosk: boolean): void {
    if (!isKiosk) {
      this.onComplete.next();
    } else {
      this.onKioskConfirm.next();
    }
  }

  private updateUserIsNotAnonymous(user?: DoodUserModel) {
    if (user) {
      return this.userService.updateUser$(
        {
          is_anonymous: false,
        },
        user.id,
      );
    } else {
      return of(user);
    }
  }

  changePassword() {
    this.isPasswordChangeInProgress = true;
    const newPassword = this.passwordForm.get('password')?.value;
    const firebaseUser = this.authFirebaseService.getCurrentUser();
    const doodUser = this.authStoreSelector.selectUser();

    if (firebaseUser) {
      this.upgradeFirebasePassword(firebaseUser, newPassword);
      return;
    }

    if (this.authFirebaseService.hasDoodAnonymousSession() && doodUser?.id) {
      const email = this.resolveUpgradeEmail(doodUser);
      if (!email) {
        this.isPasswordChangeInProgress = false;
        this.analyticsService.log({
          level: 'error',
          label: '[DOOD-3204] ComoAuthenticationStep.changePassword',
          message: { result: 'error', code: 'missing_email_for_guest_upgrade' },
        });
        this.passwordErrorMessage = 'authentication.unknown-error';
        return;
      }
      this.authFirebaseService
        .upgradeAnonymousWithEmailAndPassword(email, newPassword)
        .pipe(
          filter((firebaseIdToken): firebaseIdToken is string => !!firebaseIdToken),
          switchMap(firebaseIdToken =>
            this.userService.upgradeAnonymousUser$({}, doodUser.id, firebaseIdToken),
          ),
          switchMap(() => this.authFirebaseService.loadUser$()),
          switchMap(user => this.updateUserIsNotAnonymous(user)),
          tap(() => {
            this.isPasswordChangeInProgress = false;
            this.analyticsService.log({
              level: 'info',
              label: '[DOOD-3204] ComoAuthenticationStep.changePassword',
              message: { result: 'success', path: 'dood_guest_upgrade' },
            });
            this.finishLoyaltySession(!!this.kioskParameters.isKioskLayout);
          }),
        )
        .subscribe({
          error: error => {
            this.isPasswordChangeInProgress = false;
            this.passwordErrorMessage = this.authFirebaseService.getPasswordErrorMessage(error);
            this.analyticsService.log({
              level: 'error',
              label: '[DOOD-3204] ComoAuthenticationStep.changePassword',
              message: {
                result: 'error',
                path: 'dood_guest_upgrade',
                code: (error as { code?: string })?.code ?? null,
                message: (error as { message?: string })?.message ?? null,
              },
            });
          },
        });
      return;
    }

    this.isPasswordChangeInProgress = false;
    this.analyticsService.log({
      level: 'error',
      label: '[DOOD-3204] ComoAuthenticationStep.changePassword',
      message: { result: 'error', code: 'no_firebase_user' },
    });
    this.passwordErrorMessage = 'authentication.unknown-error';
  }

  private upgradeFirebasePassword(
    firebaseUser: NonNullable<ReturnType<AuthFirebaseService['getCurrentUser']>>,
    newPassword: string,
  ): void {
    from(updatePassword(firebaseUser, newPassword))
      .pipe(
        switchMap(() => this.authFirebaseService.loadUser$()),
        switchMap(user => this.updateUserIsNotAnonymous(user)),
        tap(() => {
          this.isPasswordChangeInProgress = false;
          this.analyticsService.log({
            level: 'info',
            label: '[DOOD-3204] ComoAuthenticationStep.changePassword',
            message: { result: 'success', path: 'firebase_update_password' },
          });
          this.finishLoyaltySession(!!this.kioskParameters.isKioskLayout);
        }),
      )
      .subscribe({
        error: error => {
          this.isPasswordChangeInProgress = false;
          this.analyticsService.log({
            level: 'error',
            label: '[DOOD-3204] ComoAuthenticationStep.changePassword',
            message: {
              result: 'error',
              path: 'firebase_update_password',
              code: error?.code ?? null,
              message: error?.message ?? null,
            },
          });
        },
      });
  }

  /** Email on the Dood profile, or a stable placeholder from the Como phone number. */
  private resolveUpgradeEmail(user: DoodUserModel): string | undefined {
    const profileEmail = user.email?.trim().toLowerCase();
    if (profileEmail) {
      return profileEmail;
    }
    const digits = this.phone?.replace(/\D/g, '');
    if (!digits) {
      return undefined;
    }
    return `como-${digits}@guest.dood.com`;
  }
}
